import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" }) : "—");
const detail = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong."; };

const STATUS_LABEL = { paid: "Paid", pending: "Pending", initiated: "Started", failed: "Failed", expired: "Expired", refunded: "Refunded" };
const STATUS_STYLE = {
  paid: { background: "rgba(16,124,60,.12)", color: "#0a7d3c" },
  refunded: { background: "rgba(200,120,0,.16)", color: "#a06a00" },
  failed: { background: "rgba(179,38,30,.12)", color: "#b3261e" },
  expired: { background: "rgba(120,120,120,.14)", color: "#5f6b66" },
  pending: { background: "rgba(180,140,20,.14)", color: "#8a6d00" },
  initiated: { background: "rgba(120,120,120,.12)", color: "#5f6b66" },
};
const money = (n) => `S$${(Number(n) || 0).toFixed(2)}`;

export const PurchasesView = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [unbooked, setUnbooked] = useState([]);
  const [showUnbooked, setShowUnbooked] = useState(false);
  const [nudging, setNudging] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminApi.purchases()); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);
  const loadUnbooked = useCallback(async () => {
    try { setUnbooked(await adminApi.unbookedBuyers()); } catch (err) { toast.error(detail(err)); }
  }, []);
  useEffect(() => { loadUnbooked(); }, [loadUnbooked]);

  const nudge = async (email) => {
    setNudging(email);
    try { const r = await adminApi.nudgeBooking(email); toast.success(r.ok ? `Nudge sent to ${r.email}` : `Could not email ${r.email}`); setUnbooked((u) => u.map((b) => (b.email === email ? { ...b, nudged_at: new Date().toISOString() } : b))); }
    catch (err) { toast.error(detail(err)); }
    finally { setNudging(""); }
  };

  const inRange = (iso) => {
    if (range === "all" || !iso) return true;
    const d = new Date(iso); const now = new Date();
    if (range === "today") return d.toDateString() === now.toDateString();
    const days = range === "week" ? 7 : 30;
    const from = new Date(now); from.setDate(now.getDate() - days);
    return d >= from;
  };
  const filtered = rows.filter((r) => inRange(r.created_at));
  const paid = filtered.filter((r) => r.status === "paid");
  const refunded = filtered.filter((r) => r.status === "refunded");
  const incomplete = filtered.filter((r) => ["failed", "expired"].includes(r.status));
  const revenue = paid.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const refundedAmt = refunded.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const undelivered = paid.filter((r) => !r.delivered).length;

  const exportCsv = async () => {
    try {
      const blob = await adminApi.exportPurchasesCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `transitionworks-purchases-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Spreadsheet downloaded");
    } catch (err) { toast.error(detail(err)); }
  };

  const requestReview = async (r) => {
    try { const res = await adminApi.requestReview(r.session_id); setRows((rows) => rows.map((x) => (x.session_id === r.session_id ? { ...x, review_requested: true } : x))); toast.success(res.ok ? `Review request sent to ${res.email}` : `Could not email ${res.email}`); }
    catch (err) { toast.error(detail(err)); }
  };

  return (
    <div data-testid="admin-purchases">
      <div className="admin-title">
        <span className="eyebrow">eBook sales</span>
        <h1>Purchases</h1>
      </div>
      <div className="admin-stats">
        <div className="admin-stat" data-testid="purchases-stat-sold"><span>eBooks sold</span><strong>{paid.length}</strong></div>
        <div className="admin-stat" data-testid="purchases-stat-revenue"><span>Net revenue</span><strong>{money(revenue)}</strong></div>
        <div className="admin-stat" data-testid="purchases-stat-refunded"><span>Refunded</span><strong>{refunded.length}{refunded.length ? ` · ${money(refundedAmt)}` : ""}</strong></div>
        <div className="admin-stat" data-testid="purchases-stat-incomplete"><span>Failed / incomplete</span><strong>{incomplete.length}</strong></div>
      </div>
      {(refunded.length > 0 || undelivered > 0) && (
        <div className="admin-toolbar" data-testid="purchases-flags" style={{ gap: 10, flexWrap: "wrap" }}>
          {refunded.length > 0 && <span className="admin-pill" style={{ background: STATUS_STYLE.refunded.background, color: STATUS_STYLE.refunded.color }}>{refunded.length} refunded · {money(refundedAmt)} returned</span>}
          {undelivered > 0 && <span className="admin-pill" style={{ background: "rgba(179,38,30,.12)", color: "#b3261e" }}>{undelivered} paid but eBook email not sent</span>}
        </div>
      )}

      <div className={`unbooked-card${unbooked.length ? " has-items" : ""}`} data-testid="unbooked-card">
        <button type="button" className="unbooked-head" onClick={() => setShowUnbooked((s) => !s)} data-testid="unbooked-toggle">
          <span><strong>Buyers who haven't booked yet</strong> — {unbooked.length} {unbooked.length === 1 ? "reader" : "readers"} can still claim a 5Ps Conversation</span>
          <span className="unbooked-caret">{showUnbooked ? "▲" : "▼"}</span>
        </button>
        {showUnbooked && (
          <div className="unbooked-body" data-testid="unbooked-body">
            {unbooked.length === 0 ? <div className="admin-empty" data-testid="unbooked-empty">Every buyer has booked their conversation. 🎉</div> : (
              <table className="admin-table">
                <thead><tr><th>Buyer email</th><th>Purchased</th><th>Nudge</th></tr></thead>
                <tbody>
                  {unbooked.map((b) => (
                    <tr key={b.email} data-testid={`unbooked-row-${b.email}`}>
                      <td><a href={`mailto:${b.email}`}>{b.email}</a></td>
                      <td className="admin-muted">{fmt(b.purchased_at)}</td>
                      <td>
                        <button className="admin-ghost" onClick={() => nudge(b.email)} disabled={nudging === b.email} data-testid={`unbooked-nudge-${b.email}`}>
                          {nudging === b.email ? "Sending..." : b.nudged_at ? "Nudge again" : "Send booking nudge"}
                        </button>
                        {b.nudged_at && <span className="admin-muted" style={{ marginLeft: 8, fontSize: ".8rem" }}>nudged {fmt(b.nudged_at)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="admin-toolbar">
        <div className="admin-filters" data-testid="purchases-range">
          {[["all", "All time"], ["today", "Today"], ["week", "7 days"], ["month", "30 days"]].map(([k, lbl]) => (
            <button key={k} className={`filter-btn${range === k ? " active" : ""}`} onClick={() => setRange(k)} data-testid={`purchases-range-${k}`}>{lbl}</button>
          ))}
        </div>
        <div className="admin-header-actions">
          <button className="admin-ghost" onClick={exportCsv} data-testid="purchases-export-btn">Export spreadsheet</button>
          <button className="admin-ghost" onClick={load} data-testid="purchases-refresh-btn">Refresh</button>
        </div>
      </div>
      <div className="admin-table-wrap">
        {loading && !rows.length ? <div className="admin-empty">Loading purchases...</div>
          : !filtered.length ? <div className="admin-empty" data-testid="purchases-empty">{rows.length ? "No purchases in this period." : "No purchases yet."}</div> : (
          <table className="admin-table" data-testid="purchases-table">
            <thead><tr><th>Date</th><th>Buyer email</th><th>Amount</th><th>Payment</th><th>eBook delivery</th><th>Review</th></tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.session_id} data-testid={`purchase-row-${r.session_id}`} className={["refunded", "failed", "expired"].includes(r.status) ? "purchase-row-flagged" : undefined}>
                  <td className="admin-muted">{fmt(r.created_at)}</td>
                  <td>{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : <span className="admin-muted">Not captured</span>}</td>
                  <td><strong style={r.status === "refunded" ? { textDecoration: "line-through", opacity: 0.6 } : undefined}>{r.currency === "SGD" ? "S$" : `${r.currency} `}{Number(r.amount || 0).toFixed(2)}</strong></td>
                  <td><span className="admin-status" style={{ ...(STATUS_STYLE[r.status] || STATUS_STYLE.initiated), padding: "3px 10px", borderRadius: 999, fontWeight: 700, fontSize: ".78rem" }} data-testid={`purchase-status-${r.session_id}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                  <td>{r.status === "paid" ? (r.delivered ? <span className="admin-pill">Email sent</span> : <span className="admin-pill" style={{ background: "rgba(180,70,47,.12)", color: "#b4462f" }}>Not sent</span>) : <span className="admin-muted">—</span>}</td>
                  <td>{r.status === "paid" && r.email ? (r.review_requested ? <span className="admin-pill" data-testid={`purchase-review-sent-${r.session_id}`}>Requested</span> : <button className="admin-link" onClick={() => requestReview(r)} data-testid={`purchase-review-btn-${r.session_id}`}>Ask for a review</button>) : <span className="admin-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export const SupportView = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminApi.supportLeads()); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (lead) => {
    try {
      const updated = await adminApi.updateSupportLead(lead.id, !lead.handled);
      setRows((r) => r.map((x) => (x.id === lead.id ? updated : x)));
      toast.success(updated.handled ? "Marked as handled" : "Reopened");
    } catch (err) { toast.error(detail(err)); }
  };

  const open = rows.filter((r) => !r.handled).length;

  return (
    <div data-testid="admin-support">
      <div className="admin-title">
        <span className="eyebrow">Ask Scottie follow-ups</span>
        <h1>Support inbox</h1>
      </div>
      <div className="admin-stats">
        <div className="admin-stat" data-testid="support-stat-total"><span>Total requests</span><strong>{rows.length}</strong></div>
        <div className="admin-stat" data-testid="support-stat-open"><span>Awaiting reply</span><strong>{open}</strong></div>
      </div>
      <div className="admin-toolbar">
        <div />
        <button className="admin-ghost" onClick={load} data-testid="support-refresh-btn">Refresh</button>
      </div>
      <div className="admin-table-wrap">
        {loading && !rows.length ? <div className="admin-empty">Loading requests...</div>
          : !rows.length ? <div className="admin-empty" data-testid="support-empty">No follow-up requests yet.</div> : (
          <table className="admin-table" data-testid="support-table">
            <thead><tr><th>Received</th><th>From</th><th>Message</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`support-row-${r.id}`}>
                  <td className="admin-muted">{fmt(r.createdAt)}</td>
                  <td><strong>{r.name || "Visitor"}</strong><br /><a href={`mailto:${r.email}`} className="admin-muted">{r.email}</a></td>
                  <td className="admin-topic">{r.message}</td>
                  <td>
                    {r.handled ? <span className="admin-pill">Handled</span> : <span className="admin-pill" style={{ background: "rgba(180,70,47,.12)", color: "#b4462f" }}>New</span>}
                    <br />
                    <button className="admin-link" onClick={() => toggle(r)} data-testid={`support-toggle-${r.id}`}>{r.handled ? "Reopen" : "Mark handled"}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


export const ReadersView = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [nudging, setNudging] = useState("");
  const [resetting, setResetting] = useState("");
  const [tEmail, setTEmail] = useState("");
  const [tPass, setTPass] = useState("");
  const [tName, setTName] = useState("");
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminApi.readers()); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const isInactive = (r) => {
    const ref = r.last_active || r.activated_at;
    if (!ref) return false;
    return Date.now() - new Date(ref).getTime() > 7 * 864e5;
  };
  const nudge = async (email) => {
    setNudging(email);
    try {
      const r = await adminApi.nudgeReader(email);
      toast.success(r.ok ? `Nudge sent to ${r.email}.` : `Saved, but the email could not be delivered to ${r.email}.`);
      load();
    } catch (err) { toast.error(detail(err)); }
    finally { setNudging(""); }
  };
  const sendReset = async (email) => {
    setResetting(email);
    try {
      const r = await adminApi.resetReaderPassword(email);
      toast.success(r.ok ? `Set-password link sent to ${r.email}.` : `Could not deliver the email to ${r.email}.`);
    } catch (err) { toast.error(detail(err)); }
    finally { setResetting(""); }
  };
  const createTest = async () => {
    if (!tEmail.trim() || tPass.length < 6) { toast.error("Enter an email and a password of at least 6 characters."); return; }
    setCreating(true);
    try {
      const r = await adminApi.createTestReader(tEmail.trim(), tPass, tName.trim());
      toast.success(r.created ? `Test account created for ${r.email}. Sign in at /read.` : `Password set for ${r.email}. Sign in at /read.`);
      setTPass(""); load();
    } catch (err) { toast.error(detail(err)); }
    finally { setCreating(false); }
  };
  const restoreMedia = async (force = false) => {
    setRestoring(true);
    try {
      const r = await adminApi.restoreMedia(force);
      const rep = r.report || {};
      const msg = `eBook: ${rep.ebook}, cover: ${rep.cover}`;
      if (rep.ebook === "unavailable") {
        toast.error(`eBook file not found in this site's storage. Please upload it via Edit website → eBook File. (${msg})`);
      } else if (rep.ebook === "outdated" || rep.cover === "outdated") {
        if (window.confirm("An older eBook/cover is already installed here. Refresh it to the latest version (this adds the new contents page and any updates)?")) {
          await restoreMedia(true);
          return;
        }
      } else {
        toast.success(`Done. ${msg}`);
      }
    } catch (err) { toast.error(detail(err)); }
    finally { setRestoring(false); }
  };

  const activeNow = rows.filter((r) => r.signed_in).length;
  const finished = rows.filter((r) => r.total && r.completed >= r.total).length;
  const inactiveCount = rows.filter(isInactive).length;
  const visible = rows.filter((r) => !q || `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="readers-view">
      <div className="admin-title">
        <span className="eyebrow">Interactive eBook</span>
        <h1>Readers</h1>
      </div>
      <div className="admin-stats">
        <div className="admin-stat" data-testid="readers-stat-total"><span>Accounts activated</span><strong>{rows.length}</strong></div>
        <div className="admin-stat" data-testid="readers-stat-active"><span>Signed in now</span><strong>{activeNow}</strong></div>
        <div className="admin-stat" data-testid="readers-stat-finished"><span>Finished all lessons</span><strong>{finished}</strong></div>
        <div className="admin-stat" data-testid="readers-stat-inactive"><span>Inactive 7 days+</span><strong>{inactiveCount}</strong></div>
      </div>
      <div className="admin-toolbar">
        <label className="searchbox"><span>⌕</span><input type="search" placeholder="Search reader name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="readers-search" /></label>
        <button className="admin-ghost admin-ghost-dark" onClick={load} data-testid="readers-refresh">Refresh</button>
      </div>
      <div data-testid="create-test-reader" style={{ background: "rgba(255,255,255,.65)", border: "1px solid var(--line)", borderRadius: 16, padding: "16px 18px", marginBottom: 16 }}>
        <p style={{ margin: "0 0 4px", fontWeight: 800, color: "var(--green)" }}>Create a test reader account</p>
        <p className="admin-muted" style={{ fontSize: ".82rem", margin: "0 0 12px", maxWidth: 640 }}>Make a login to try the eBook reader without a purchase. It signs in straight away at <strong>/read</strong> (no forced password change). Use an existing email to just reset its password.</p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input type="email" placeholder="Email" value={tEmail} onChange={(e) => setTEmail(e.target.value)} data-testid="test-reader-email" style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid #cdd6d1", minWidth: 220 }} />
          <input type="text" placeholder="Password (min 6)" value={tPass} onChange={(e) => setTPass(e.target.value)} data-testid="test-reader-password" style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid #cdd6d1", minWidth: 160 }} />
          <input type="text" placeholder="Name (optional)" value={tName} onChange={(e) => setTName(e.target.value)} data-testid="test-reader-name" style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid #cdd6d1", minWidth: 160 }} />
          <button className="btn btn-gold" onClick={createTest} disabled={creating} data-testid="test-reader-create">{creating ? "Creating..." : "Create / reset"}</button>
        </div>
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
          <p className="admin-muted" style={{ fontSize: ".82rem", margin: "0 0 10px", maxWidth: 640 }}>New site with no eBook pages? Restore the uploaded eBook and cover from storage in one click (needed once on a freshly deployed site).</p>
          <button className="admin-ghost admin-ghost-dark" onClick={() => restoreMedia(false)} disabled={restoring} data-testid="restore-media-btn">{restoring ? "Restoring..." : "Restore / refresh eBook on this site"}</button>
        </div>
      </div>
      <div className="admin-table-wrap">
        {loading && !rows.length ? <div className="admin-empty">Loading readers...</div> : !visible.length ? <div className="admin-empty" data-testid="readers-empty">No reader accounts yet. Buyers appear here once they activate their eBook.</div> : (
          <table className="admin-table" data-testid="readers-table">
            <thead><tr><th>Reader</th><th>Activated</th><th>Progress</th><th>Reflections</th><th>Notes</th><th>Bookmarks</th><th>Last active</th><th>Currently reading</th><th>Actions</th></tr></thead>
            <tbody>
              {visible.map((r) => {
                const pct = r.total ? Math.round((r.completed / r.total) * 100) : 0;
                const inactive = isInactive(r);
                return (
                  <tr key={r.email} data-testid={`reader-row-${r.email}`}>
                    <td>
                      <strong>{r.name || "Reader"}</strong>{r.signed_in && <span className="admin-pill" style={{ marginLeft: 8, background: "rgba(16,124,60,.12)", color: "#0a7d3c" }}>Online</span>}
                      <br /><a href={`mailto:${r.email}`} className="admin-muted">{r.email}</a>
                    </td>
                    <td className="admin-muted">{fmt(r.activated_at)}</td>
                    <td>
                      <div className="reader-prog-cell">
                        <div className="reader-prog-bar"><span style={{ width: `${pct}%` }} /></div>
                        <small>{r.completed}/{r.total} done · {pct}%</small>
                      </div>
                    </td>
                    <td>{r.reflections}</td>
                    <td>{r.notes}</td>
                    <td>{r.bookmarks}</td>
                    <td className="admin-muted">{fmt(r.last_active)}{inactive && <><br /><span className="admin-pill" style={{ background: "rgba(180,70,47,.12)", color: "#b4462f" }}>Inactive</span></>}</td>
                    <td className="admin-topic">{r.last_lesson_title || <span className="admin-muted">Not started</span>}</td>
                    <td className="reader-actions-cell">
                      <button className="admin-link" onClick={() => sendReset(r.email)} disabled={resetting === r.email} data-testid={`reader-reset-${r.email}`}>{resetting === r.email ? "Sending..." : "Send reset link"}</button>
                      {inactive && <button className="admin-link" onClick={() => nudge(r.email)} disabled={nudging === r.email} data-testid={`reader-nudge-${r.email}`}>{nudging === r.email ? "Sending..." : "Send nudge"}</button>}
                      {r.nudged_at && <span className="admin-muted" style={{ fontSize: ".72rem" }}>nudged {fmt(r.nudged_at)}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


export const DataView = ({ onUnauthorized }) => {
  const [items, setItems] = useState([]);
  const [backup, setBackup] = useState(null);
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const info = await adminApi.resetDataInfo(); setItems(info.items || []); setBackup(info.backup || null); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const chosen = items.filter((i) => sel[i.key]);
  const chosenKeys = chosen.map((i) => i.key);
  const chosenTotal = chosen.reduce((s, i) => s + (i.count || 0), 0);
  const allOn = items.length > 0 && chosenKeys.length === items.length;
  const selectAll = () => setSel(allOn ? {} : Object.fromEntries(items.map((i) => [i.key, true])));

  const doReset = async () => {
    if (confirm !== "RESET" || !chosenKeys.length) return;
    if (!window.confirm(`This will delete ${chosenTotal} record(s) across ${chosenKeys.length} data set(s). You can undo this once. Continue?`)) return;
    setBusy(true);
    try {
      const r = await adminApi.resetData(chosenKeys, "RESET");
      toast.success(`Reset ${r.total} record(s). You can undo below.`);
      setSel({}); setConfirm("");
      load();
    } catch (err) { toast.error(detail(err)); }
    finally { setBusy(false); }
  };
  const doUndo = async () => {
    setUndoing(true);
    try {
      const r = await adminApi.resetDataUndo();
      const total = Object.values(r.restored || {}).reduce((s, n) => s + n, 0);
      toast.success(`Restored ${total} record(s).`);
      load();
    } catch (err) { toast.error(detail(err)); }
    finally { setUndoing(false); }
  };

  return (
    <div data-testid="data-view">
      <div className="admin-title">
        <span className="eyebrow">Danger zone</span>
        <h1>Reset data</h1>
      </div>

      {backup && (
        <div className="admin-alert" data-testid="data-undo-bar" style={{ background: "rgba(224,168,58,.12)", border: "1px solid rgba(224,168,58,.4)", borderRadius: 14, padding: "16px 18px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <strong>A backup is available to undo.</strong>
            <div className="admin-muted" style={{ fontSize: ".82rem" }}>Reset on {fmt(backup.created_at)} · {backup.total} record(s) across {backup.collections?.length || 0} data set(s). Restoring clears this backup.</div>
          </div>
          <button className="btn btn-gold" onClick={doUndo} disabled={undoing} data-testid="data-undo-btn">{undoing ? "Restoring..." : "Undo reset"}</button>
        </div>
      )}

      <p className="admin-muted" style={{ maxWidth: 720, marginBottom: 18 }}>
        Pick the data you want to clear. Your website content, settings, Stripe keys, images and the uploaded eBook are never touched. The most recent reset can be undone once.
      </p>

      <div className="admin-toolbar">
        <button className="admin-ghost admin-ghost-dark" onClick={selectAll} data-testid="data-select-all">{allOn ? "Clear selection" : "Select all"}</button>
        <button className="admin-ghost admin-ghost-dark" onClick={load} data-testid="data-refresh">Refresh</button>
      </div>

      <div className="admin-table-wrap">
        {loading && !items.length ? <div className="admin-empty">Loading...</div> : (
          <table className="admin-table" data-testid="data-table">
            <thead><tr><th style={{ width: 44 }}></th><th>Data set</th><th>Records</th></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.key} data-testid={`data-row-${i.key}`}>
                  <td><input type="checkbox" checked={!!sel[i.key]} onChange={() => toggle(i.key)} disabled={!i.count} data-testid={`data-check-${i.key}`} style={{ width: 18, height: 18 }} /></td>
                  <td><strong>{i.label}</strong></td>
                  <td className={i.count ? "" : "admin-muted"}>{i.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 22, padding: "18px 20px", border: "1px solid rgba(179,38,30,.35)", background: "rgba(179,38,30,.05)", borderRadius: 14, maxWidth: 560 }}>
        <p style={{ margin: "0 0 12px", fontWeight: 700, color: "#b3261e" }}>Confirm reset</p>
        <p className="admin-muted" style={{ fontSize: ".84rem", margin: "0 0 12px" }}>
          {chosenKeys.length ? `${chosenTotal} record(s) across ${chosenKeys.length} data set(s) will be deleted.` : "Select at least one data set above."} Type <strong>RESET</strong> to enable the button.
        </p>
        <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESET" data-testid="data-confirm-input" style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #cdd6d1", width: 200, marginRight: 12 }} />
        <button className="btn" onClick={doReset} disabled={busy || confirm !== "RESET" || !chosenKeys.length} data-testid="data-reset-btn" style={{ background: confirm === "RESET" && chosenKeys.length ? "#b3261e" : "#d9b0ac", color: "#fff", border: "none" }}>{busy ? "Resetting..." : "Reset selected data"}</button>
      </div>
    </div>
  );
};
